import { useEffect, useState } from 'react';

/**
 * Tracks whether the browser currently has a connection.
 *
 * `navigator.onLine` only reports whether the machine has a network interface
 * up, so it can read `true` behind a captive portal or a dropped VPN. It is
 * dependable in the direction this is used for: when the browser flips it to
 * `false`, nothing will reach the backend, which is the case the offline
 * banner exists for. Requests that fail while `onLine` is `true` stay the
 * responsibility of the screen that made them.
 */
export function useIsOnline(): boolean {
  const [isOnline, setIsOnline] = useState<boolean>(() => navigator.onLine);

  useEffect(() => {
    const handleOnline = (): void => setIsOnline(true);
    const handleOffline = (): void => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // The connection can drop between the first render and this effect, and
    // that event lands before the listeners exist. Re-read so the banner does
    // not stay hidden until the next flip.
    setIsOnline(navigator.onLine);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}
